/**
 * The markup editor's rules: which options are active, where the placeholders are, the instant lint and the
 * HTML formatter. Pure functions with no CodeMirror import, so the page and the tests can use them without
 * loading the editor.
 *
 * The placeholder rules copy TutorPortalAPI's EmailWordingRenderer, which stays authoritative: the API
 * checks every preview and every save. This is only the instant hint while typing.
 */

/** A placeholder an email declares: {{Name}}, what it holds, and whether the wording must keep it. */
export interface MarkupToken {
  name: string;
  description: string;
  required: boolean;
}

/** One editor option per sub-switch of emailtemplates.rich-editor. */
export interface MarkupEditorOptions {
  highlight: boolean;
  placeholders: boolean;
  autocomplete: boolean;
  lint: boolean;
  lineNumbers: boolean;
  autoclose: boolean;
  format: boolean;
  search: boolean;
  wrap: boolean;
}

export const NO_MARKUP_OPTIONS: MarkupEditorOptions = {
  highlight: false, placeholders: false, autocomplete: false, lint: false, lineNumbers: false,
  autoclose: false, format: false, search: false, wrap: false
};

/** A CodeMirror extension the editor turns on. */
export type MarkupFeature =
  'html-language' | 'highlight' | 'placeholders' | 'autocomplete' | 'lint' | 'line-numbers' |
  'autoclose-tags' | 'close-brackets' | 'format' | 'search' | 'wrap';

/**
 * The extensions to build for these options. HTML highlighting, tag closing and the Format button apply only
 * to an HTML wording; a plain-text one is edited as plain text. Tag closing needs the HTML language even
 * when highlighting is off (it reads the syntax tree), so the language is loaded for either.
 */
export function resolveFeatures(options: MarkupEditorOptions | null, isHtml: boolean): MarkupFeature[] {
  const o = options || NO_MARKUP_OPTIONS;
  const features: MarkupFeature[] = [];
  if (isHtml && (o.highlight || o.autoclose)) { features.push('html-language'); }
  if (isHtml && o.highlight) { features.push('highlight'); }
  if (o.placeholders) { features.push('placeholders'); }
  if (o.autocomplete) { features.push('autocomplete'); }
  if (o.lint) { features.push('lint'); }
  if (o.lineNumbers) { features.push('line-numbers'); }
  if (isHtml && o.autoclose) { features.push('autoclose-tags'); }
  if (o.autoclose) { features.push('close-brackets'); }
  if (isHtml && o.format) { features.push('format'); }
  if (o.search) { features.push('search'); }
  if (o.wrap) { features.push('wrap'); }
  return features;
}

// ── Placeholders ─────────────────────────────────────────────────────────────────────────────────

/** EmailWordingRenderer's placeholder pattern, exactly. Names are case-sensitive. */
export const PLACEHOLDER_PATTERN = /\{\{\s*([A-Za-z][A-Za-z0-9]*)\s*\}\}/g;

export type PlaceholderKind = 'known' | 'required' | 'unknown' | 'malformed';

/** One placeholder, or one malformed "{{", found in a text. */
export interface PlaceholderSpan {
  from: number;
  to: number;
  /** The name for a well-formed placeholder; null for a malformed "{{". */
  name: string | null;
  kind: PlaceholderKind;
  /** The token's description, or why it is flagged. */
  title: string;
}

function tokenMap(tokens: MarkupToken[] | null): Map<string, MarkupToken> {
  return new Map((tokens || []).map(t => [t.name, t] as [string, MarkupToken]));
}

function wellFormed(text: string): { from: number; to: number; name: string }[] {
  const found: { from: number; to: number; name: string }[] = [];
  const re = new RegExp(PLACEHOLDER_PATTERN.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || '')) !== null) {
    found.push({ from: m.index, to: m.index + m[0].length, name: m[1] });
  }
  return found;
}

/** Every "{{" as the API counts them: left to right, not overlapping. */
function openings(text: string): number[] {
  const at: number[] = [];
  for (let i = (text || '').indexOf('{{'); i >= 0; i = text.indexOf('{{', i + 2)) {
    at.push(i);
  }
  return at;
}

/**
 * The placeholders in a text, in order: each well-formed one as known, required or unknown, and each "{{"
 * that does not open one as malformed. The API refuses a wording whose "{{" count differs from its
 * well-formed count; a "{{" is matched to a placeholder starting at it or one character on (so "{{{Name}}"
 * is fine, as it is to the API).
 */
export function findPlaceholders(text: string, tokens: MarkupToken[] | null): PlaceholderSpan[] {
  const byName = tokenMap(tokens);
  const spans: PlaceholderSpan[] = wellFormed(text).map(p => {
    const token = byName.get(p.name);
    return {
      from: p.from, to: p.to, name: p.name,
      kind: !token ? 'unknown' : token.required ? 'required' : 'known',
      title: token ? token.description + (token.required ? ' (must stay in the wording)' : '')
        : `{{${p.name}}} is not a placeholder of this email.`
    } as PlaceholderSpan;
  });
  const starts = new Set(spans.map(s => s.from));
  openings(text).filter(at => !starts.has(at) && !starts.has(at + 1)).forEach(at => {
    const close = text.indexOf('}}', at + 2);
    const lineEnd = text.indexOf('\n', at);
    const to = close >= 0 && (lineEnd < 0 || close < lineEnd) ? close + 2 : at + 2;
    spans.push({ from: at, to, name: null, kind: 'malformed', title: 'A placeholder is malformed. Write each one as {{Name}}.' });
  });
  return spans.sort((a, b) => a.from - b.from);
}

/** One instant-lint finding in the edited text. */
export interface MarkupDiagnostic {
  from: number;
  to: number;
  severity: 'error';
  message: string;
}

export interface MarkupLintResult {
  /** Unknown and malformed placeholders, positioned in the edited text. */
  diagnostics: MarkupDiagnostic[];
  /** Required placeholders used in neither the edited text nor `alsoUses` (the subject). */
  missingRequired: MarkupToken[];
}

/**
 * The API's placeholder rules, applied as you type: only this email's placeholders, each "{{" well formed,
 * and every required one kept somewhere in the wording. `alsoUses` is the rest of the wording (the subject):
 * a required placeholder there counts as kept.
 */
export function lintMarkup(text: string, tokens: MarkupToken[] | null, alsoUses: string = ''): MarkupLintResult {
  const spans = findPlaceholders(text, tokens);
  const diagnostics: MarkupDiagnostic[] = spans
    .filter(s => s.kind === 'unknown' || s.kind === 'malformed')
    .map(s => ({
      from: s.from, to: s.to, severity: 'error' as const,
      message: s.kind === 'malformed' ? s.title
        : `{{${s.name}}} is not a placeholder of this email. It can use: ${(tokens || []).map(t => `{{${t.name}}}`).join(', ')}.`
    }));
  const used = new Set([...wellFormed(text), ...wellFormed(alsoUses || '')].map(p => p.name));
  const missingRequired = (tokens || []).filter(t => t.required && !used.has(t.name));
  return { diagnostics, missingRequired };
}

// ── Formatter ────────────────────────────────────────────────────────────────────────────────────

/** Elements that start their own line. Anything else (b, a, span, img, br…) stays in the flow of the text. */
const BLOCK = new Set([
  'address', 'article', 'aside', 'blockquote', 'body', 'caption', 'center', 'col', 'colgroup', 'dd', 'div', 'dl',
  'dt', 'fieldset', 'figcaption', 'figure', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head',
  'header', 'hr', 'html', 'li', 'link', 'main', 'meta', 'nav', 'ol', 'p', 'pre', 'section', 'style', 'script',
  'table', 'tbody', 'td', 'tfoot', 'th', 'thead', 'title', 'tr', 'ul'
]);
const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'source', 'track', 'wbr']);
/** Content kept byte for byte: whitespace inside these is meaningful or is code. */
const RAW = new Set(['pre', 'script', 'style', 'textarea']);

interface Piece {
  kind: 'open' | 'close' | 'void' | 'raw' | 'comment' | 'text';
  text: string;
  block: boolean;
}

/** The end of a tag starting at `from` ("<"), stepping over quoted attribute values; -1 when unterminated. */
function tagEnd(html: string, from: number): number {
  let quote: string | null = null;
  for (let i = from + 1; i < html.length; i++) {
    const c = html[i];
    if (quote) {
      if (c === quote) { quote = null; }
    } else if (c === '"' || c === '\'') {
      quote = c;
    } else if (c === '>') {
      return i;
    }
  }
  return -1;
}

function tokenise(html: string): Piece[] {
  const pieces: Piece[] = [];
  let text = '';
  const flush = () => { if (text) { pieces.push({ kind: 'text', text, block: false }); text = ''; } };
  let i = 0;
  while (i < html.length) {
    const rest = html.startsWith('<', i) ? html.slice(i, i + 4) : '';
    if (rest.startsWith('<!--')) {
      const end = html.indexOf('-->', i + 4);
      const stop = end < 0 ? html.length : end + 3;
      flush();
      pieces.push({ kind: 'comment', text: html.slice(i, stop), block: false });
      i = stop;
      continue;
    }
    const m = /^<(\/?)([A-Za-z][A-Za-z0-9-]*)/.exec(html.slice(i, i + 64));
    const decl = /^<[!?]/.test(html.slice(i, i + 2));
    if (!m && !decl) {
      text += html[i++];
      continue;
    }
    const end = tagEnd(html, i);
    if (end < 0) {
      // An unterminated tag is left as text rather than guessed at.
      text += html.slice(i);
      break;
    }
    flush();
    const tag = html.slice(i, end + 1);
    i = end + 1;
    if (decl) {
      pieces.push({ kind: 'void', text: tag, block: true });
      continue;
    }
    const closing = m[1] === '/';
    const name = m[2].toLowerCase();
    const block = BLOCK.has(name);
    if (closing) {
      pieces.push({ kind: 'close', text: tag, block });
    } else if (RAW.has(name)) {
      const close = new RegExp(`</${name}\\s*>`, 'i').exec(html.slice(i));
      const stop = close ? i + close.index + close[0].length : html.length;
      pieces.push({ kind: 'raw', text: tag + html.slice(i, stop), block });
      i = stop;
    } else if (VOID.has(name) || /\/\s*>$/.test(tag)) {
      pieces.push({ kind: 'void', text: tag, block });
    } else {
      pieces.push({ kind: 'open', text: tag, block });
    }
  }
  flush();
  return pieces;
}

/**
 * Lays HTML out one block element per line, indented by nesting. It only ever changes whitespace, and only
 * where whitespace carries no meaning: a whitespace-only run beside a block element becomes a line break and
 * indent, and a break is added between two block tags that touch. It never adds whitespace between a block
 * tag and text or an inline element (so "<p>Hi</p>" stays on one line, and nothing opens a gap beside an
 * image in a table cell). Tags, attributes, text, placeholders and the contents of pre, script, style and
 * textarea are kept exactly. Running it twice gives the same result as running it once.
 */
export function formatHtml(html: string, indentUnit: string = '  '): string {
  if (!html) {
    return html;
  }
  const pieces = tokenise(html);
  const isWs = (p: Piece | undefined) => !!p && p.kind === 'text' && p.text.trim() === '';
  let out = '';
  let level = 0;
  let pendingBreak = false;

  pieces.forEach((p, idx) => {
    const prev = pieces[idx - 1];
    const next = pieces[idx + 1];
    if (isWs(p)) {
      if ((prev && prev.block) || (next && next.block) || !prev || !next) {
        pendingBreak = true;
      } else {
        out += p.text;
      }
      return;
    }
    let content = p.text;
    let breakAfter = false;
    if (p.kind === 'text') {
      // Whitespace at either end of a text run is re-laid only where it touches a block tag (or the
      // start or end of the wording); whitespace inside the run is kept exactly.
      const lead = /^\s+/.exec(content);
      if (lead && (!prev || prev.block)) {
        content = content.slice(lead[0].length);
        pendingBreak = true;
      }
      const trail = /\s+$/.exec(content);
      if (trail && (!next || next.block)) {
        content = content.slice(0, content.length - trail[0].length);
        breakAfter = true;
      }
    }
    if (p.kind === 'close' && p.block) {
      level = Math.max(0, level - 1);
    }
    const touchingBlocks = !!prev && prev.block && p.block;
    if ((pendingBreak || touchingBlocks) && out !== '') {
      out += '\n' + indentUnit.repeat(level);
    }
    pendingBreak = breakAfter;
    out += content;
    if (p.kind === 'open' && p.block) {
      level++;
    }
  });

  return /\r?\n$/.test(html) ? out + '\n' : out;
}
